import React, { useState, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from '@/contexts/AuthContext';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import NewButton from "@/components/ui/NewButton";
import PremiumLoader from "@/components/ui/PremiumLoader";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import logoLight from '@/components/logo/logo light.png';
import logoDark from '@/components/logo/logo dark.png';
import { useTheme } from 'next-themes';

const LogIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { resolvedTheme } = useTheme();
  const { signIn, user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Show message from sign up redirect
  useEffect(() => {
    if (location.state?.message) {
      // Message will be shown via toast from AuthContext
    }
  }, [location.state]);

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/" replace />;
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    
    // Final validation before submit
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (!password) {
      return;
    }

    const result = await signIn(email, password);
    
    // If sign in was successful, redirect to home page
    if (result.success) {
      navigate('/');
    } else {
      if (result.error?.message === 'Invalid login credentials') {
        setErrorMessage('Invalid email or password. Please try again.');
      } else {
        setErrorMessage('An unexpected error occurred. Please try again later.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="space-y-4">
          {/* Back button + Logo row */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-medium border border-gray-300 bg-transparent transition-all hover:scale-105 active:scale-95 text-foreground"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
            <Link to="/" className="inline-block">
              <img src={resolvedTheme === 'dark' ? logoDark : logoLight} alt="Fork It" className="h-7 w-auto" />
            </Link>
            <div className="w-16" />
          </div>
          {/* Title + subtitle */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Welcome Back
            </h2>
            <p className="text-muted-foreground">
              Sign in to continue your culinary journey
            </p>
          </div>
        </div>

        {/* Success message from sign up */}
        {location.state?.message && (
          <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-sm text-green-700 dark:text-green-400">{location.state.message}</p>
          </div>
        )}

        {/* Sign In Form */}
        <Card className="bg-card dark:bg-zinc-800 backdrop-blur-sm border-border dark:border-zinc-700 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl text-foreground dark:text-white text-center">
              Sign In
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {errorMessage && (
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
                  <p className="text-sm text-center text-red-700 dark:text-red-400">{errorMessage}</p>
                </div>
              )}
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-foreground dark:text-zinc-200">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  className="border-input dark:bg-zinc-700 dark:border-zinc-600 dark:text-white dark:placeholder:text-zinc-400 focus:border-lime focus:ring-lime/20"
                />
                {emailError && (
                  <p className="text-sm text-red-500">{emailError}</p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-foreground dark:text-zinc-200">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="border-input dark:bg-zinc-700 dark:border-zinc-600 dark:text-white dark:placeholder:text-zinc-400 focus:border-lime focus:ring-lime/20 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground dark:hover:text-white"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm text-muted-foreground hover:text-lime hover:underline"
                >
                  Forgot Password?
                </Link>
              </div>

              <div className="flex justify-center pt-2">
                <NewButton
                  type="submit"
                  disabled={loading || !!emailError || !email || !password}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <PremiumLoader size="sm" text="" />
                      <span>Signing In...</span>
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </NewButton>
              </div>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border dark:border-zinc-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card dark:bg-zinc-800 text-muted-foreground">OR</span>
                </div>
              </div>

              {/* Google Auth */}
              <GoogleAuthButton />
            </form>
          </CardContent>
        </Card>

        {/* Sign Up Link */}
        <div className="text-center">
          <p className="text-muted-foreground">
            Don't have an account?{' '}
            <Link
              to="/signup"
              className="text-lime hover:text-lime-600 font-semibold transition-colors duration-200"
            >
              Create Account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LogIn;
